/// <amd-module name="VSSUI/Navigation/ViewStateNavigation" />
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IViewState, IViewStateChangeEvent, IViewStateNotifyDelegate, ViewStateEventNames } from "../Utilities/ViewState";
import { IVssPageContext } from "VSS/Platform/Context";
import { INavigationStateChangedEvent, IVssHistoryService, StateMergeOptions } from "VSS/Platform/History";
import { IVssLocationService } from "VSS/Platform/Location";

/** @deprecated */
export const NAVIGATION_POPPED_EVENT = "navigationPopped";
const UPDATE_NAVIGATION_STATE_EVENT = "updateNavigationState";

/**
 * The behavior when the nav param changes - whether to update the current history entry or add a new one
 */
/** @deprecated */
export const enum HistoryBehavior {
    /**
     * Don't update the browser history/URL
     */
    none = 0,

    /**
     * Replace the existing history entry with the new url (replaceState)
     */
    replace = 1,

    /**
     * Add a new history entry (pushState)
     */
    newEntry = 2
}

/**
 * Describes a navigation parameter - a key/value pair used in the URL hash/querystring
 * that maps to a view state value
 */
/** @deprecated */
export interface INavigationParameter {
    /**
     * Get the current value to persist to the URL
     */
    getValue?: () => string;

    /**
     * Set the value of the parameter based on the value retrieved from the URL
     */
    setValue: (newValue: string) => void;
}

/**
 * Lookup of navigation parameters by the navigation parameter name
 */
/** @deprecated */
export interface INavigationParameters {
    [parameterName: string]: INavigationParameter;
}

/**
 * Class that updates the URL of the page based on the view state, and updates the view state on
 * naviation pop-state (back/forward) events.
 */
/** @deprecated */
export class ViewStateNavigation<T> {
    private viewState: IViewState<T>;
    private viewStateNotifyDelegate: IViewStateNotifyDelegate<T>;
    private navigationParameters: INavigationParameters;
    private getUpdateBehavior: (event: IViewStateChangeEvent<T>) => HistoryBehavior;

    private pageContext: IVssPageContext;
    private navHistoryService: IVssHistoryService;
    private behaviorOverride: HistoryBehavior;

    /**
     * Create a new ViewStateNavigation
     *
     * @param pageContext Current page context
     * @param viewState The view state to subscribe to and update
     * @param viewStateNotifyDelegate Method to signify to the view state that a navigation change is occurring.
     * @param parameters Mapping of navigation parameters
     * @param initializeViewState If true, initialize the view state from the current URL
     */
    constructor(
        pageContext: IVssPageContext,
        viewState: IViewState<T>,
        viewStateNotifyDelegate: IViewStateNotifyDelegate<T>,
        parameters: INavigationParameters,
        initializeViewState: boolean,
        getUpdateBehavior: (event: IViewStateChangeEvent<T>) => HistoryBehavior
    ) {
        this.pageContext = pageContext;
        this.viewState = viewState;
        this.viewStateNotifyDelegate = viewStateNotifyDelegate;
        this.navigationParameters = parameters;
        this.getUpdateBehavior = getUpdateBehavior;
        this.navHistoryService = pageContext.getService<IVssHistoryService>("IVssHistoryService");

        if (initializeViewState) {
            this._updateFromNavigationState(this.navHistoryService.getState());
        }

        this.navHistoryService.subscribe(this._onNavigate);
        this.viewState.subscribe(this._onViewStateChanged, ViewStateEventNames.viewStateChanged);
    }

    public dispose() {
        this.navHistoryService.unsubscribe(this._onNavigate);
        this.viewState.unsubscribe(this._onViewStateChanged, ViewStateEventNames.viewStateChanged);
    }

    /**
     * Perform a bulk set of changes to the view state and make a single update
     * to the browser history -- either adding a new entry or replacing the existing entry.
     * The behavior specified here overrides the behavior for individual nav parameters.
     *
     * @param behavior Whether to add a new history entry or replace the existing entry after modifying the view state
     * @param updateViewStateCalback Method where state should be updated.
     */
    public updateNavigationState(behavior: HistoryBehavior, updateViewStateCallback: () => void) {
        this.behaviorOverride = behavior;
        this.viewStateNotifyDelegate(null, UPDATE_NAVIGATION_STATE_EVENT, this.viewState, UPDATE_NAVIGATION_STATE_EVENT, () => {
            updateViewStateCallback();
        });
        delete this.behaviorOverride;
    }

    /**
     * Create an observable for a URL on this view which gets updated whenever the view state has changed.
     *
     * @param state Values (query parameters and route values) to use when generating the url.
     * @param mergeOptions Options around whether the provided state just provides overrides for the current navigation
     *        state or a full set of state values. The default is to merge with the current navigation state.
     */
    public createObservableUrl(
        state: { [key: string]: string },
        mergeOptions: StateMergeOptions = StateMergeOptions.currentState,
        routeId?: string
    ): IObservableViewStateUrl {
        return new ObservableViewStateUrl(this.pageContext, this.viewState, state, mergeOptions, routeId);
    }

    private _onViewStateChanged = (e: IViewStateChangeEvent<T>) => {
        let behavior: HistoryBehavior;
        if (this.behaviorOverride !== undefined) {
            behavior = this.behaviorOverride;
        } else {
            behavior = this.getUpdateBehavior(e);
        }

        if (behavior === HistoryBehavior.none) {
            return;
        }

        const navigationState = this.navHistoryService.getState();

        let hasChanges = false;
        for (let paramKey in this.navigationParameters) {
            const value = this.navigationParameters[paramKey].getValue();
            if (!value) {
                if (navigationState[paramKey] !== undefined) {
                    delete navigationState[paramKey];
                    hasChanges = true;
                }
            } else {
                if (navigationState[paramKey] !== value) {
                    navigationState[paramKey] = value;
                    hasChanges = true;
                }
            }
        }

        if (hasChanges) {
            if (behavior === HistoryBehavior.newEntry) {
                this.navHistoryService.pushState(navigationState);
            } else {
                this.navHistoryService.replaceState(navigationState);
            }
        }
    };

    private _onNavigate = (popStateEvent: INavigationStateChangedEvent) => {
        this.viewStateNotifyDelegate(null, NAVIGATION_POPPED_EVENT, this.viewState, NAVIGATION_POPPED_EVENT, () => {
            if (popStateEvent.newState) {
                this._updateFromNavigationState(popStateEvent.newState.state);
            }
        });
    };

    private _updateFromNavigationState(navigationState: { [key: string]: string }) {
        for (let paramKey in this.navigationParameters) {
            this.navigationParameters[paramKey].setValue(navigationState[paramKey]);
        }
    }

    public updateFromNavigationState(parameter: string): void {
        const navParam = this.navigationParameters[parameter];
        const state = this.navHistoryService.getState();
        if (navParam.getValue() !== state[parameter]) {
            navParam.setValue(state[parameter]);
        }
    }
}

/**
 * An observable url which is updated when the view state changes.
 */
/** @deprecated */
export interface IObservableViewStateUrl extends IObservableValue<string> {
    /**
     * Method to force the url to be updated based on the current navigation state.
     * This is called automatically when the view state has been changed.
     */
    updateUrl(): string;

    /**
     * Modify the state values used by this url observable (and update the url if necessary).
     */
    setState(state: { [key: string]: string }): void;

    /**
     * Cleanup subscriptions and dispose the observable.
     */
    dispose(): void;
}

class ObservableViewStateUrl extends ObservableValue<string | undefined> implements IObservableViewStateUrl {
    private pageContext: IVssPageContext;
    private viewState: IViewState<any>;
    private state: { [key: string]: string };
    private mergeOptions: StateMergeOptions;
    private routeId?: string;

    constructor(
        pageContext: IVssPageContext,
        viewState: IViewState<any>,
        state: { [key: string]: string },
        mergeOptions: StateMergeOptions,
        routeId?: string
    ) {
        super(undefined);

        this.pageContext = pageContext;
        this.viewState = viewState;
        this.state = state;
        this.mergeOptions = mergeOptions;
        this.routeId = routeId;

        this.updateUrl();

        this.viewState.subscribe(this.onViewStateChanged, ViewStateEventNames.viewStateChanged);
    }

    public updateUrl(): string {
        let url: string;
        if (this.routeId) {
            const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");
            url = locationService.routeUrl(this.routeId, this.state);
        } else {
            const navHistoryService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
            url = navHistoryService.generateUrl(this.state, this.mergeOptions);
        }
        if (this.value !== url) {
            this.value = url;
        }
        return url;
    }

    public setState(state: { [key: string]: string }): void {
        this.state = state;
        this.updateUrl();
    }

    private onViewStateChanged = () => {
        this.updateUrl();
    };

    public dispose() {
        this.viewState.unsubscribe(this.onViewStateChanged, ViewStateEventNames.viewStateChanged);
    }
}
